import { useEffect, useRef, useState } from "react";
import ParlorRevisitLeadModal from "./ParlorRevisitLeadModal";
import { buildParlorRevisitLeadMessage } from "../utils/parlorRevisitLeadMessage";
import { customEvent, OverlayModal } from "goi_common";

export const PARLOR_REVISIT_ATTRIBUTION_STORAGE_KEY = "parlor_revisit_attribution";
const PARLOR_REVISIT_SUBMITTED_KEY_PREFIX = "goi_parlor_revisit_submitted_";

/** 세션 내 재방문 모달 노출 여부 - 다른 페이지(로그인 등)의 재방문 귀속 이벤트에서 사용 */
export function hasParlorRevisitAttribution(): boolean {
  try {
    return !!sessionStorage.getItem(PARLOR_REVISIT_ATTRIBUTION_STORAGE_KEY);
  } catch {
    return false;
  }
}

function hasSubmittedRevisitLead(parlorId: string): boolean {
  try {
    return !!localStorage.getItem(PARLOR_REVISIT_SUBMITTED_KEY_PREFIX + parlorId);
  } catch {
    return false;
  }
}

function markRevisitLeadSubmitted(parlorId: string) {
  try {
    localStorage.setItem(PARLOR_REVISIT_SUBMITTED_KEY_PREFIX + parlorId, "true");
  } catch {
    // localStorage 접근 불가 - 무시
  }
}

/**
 * isRevisit일 때만 부모에서 조건부 마운트되어야 함 - 그래야 첫방문자에게
 * 모달/이벤트가 새지 않는다.
 */
const ParlorRevisitExperimentGate = ({
  parlorId,
  companyName,
  region,
}: {
  parlorId: string;
  companyName: string;
  region?: string;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    try {
      sessionStorage.setItem(PARLOR_REVISIT_ATTRIBUTION_STORAGE_KEY, "true");
    } catch {
      // sessionStorage 접근 불가 - 무시
    }

    if (hasSubmittedRevisitLead(parlorId)) return;

    customEvent({
      newGtm: {
        action: "uv_revisit_parlor_modal",
      },
    });

    setIsModalOpen(true);
  }, [parlorId]);

  return (
    <OverlayModal
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      closeOnOverlayClick
      modalContentStyle={{
        minWidth: "340px",
        overflow: "scroll",
        borderRadius: "20px",
      }}
      overlayModalContent={
        <ParlorRevisitLeadModal
          copy={{
            caption: `${companyName} 고민 중이신가요?`,
            title: "장례 총 비용 빠르게 알아보기",
            step1Description: "예상 조문객수를 선택하시면 상세 안내가 가능합니다.",
            step2Description:
              "입력해주신 전화번호로 장례식장 견적 등 상세 내용을 빠르게 안내해드릴게요.",
            submitLabel: "동의하고 상세 안내 받기",
          }}
          buildSlackMessage={(mournerCount) =>
            buildParlorRevisitLeadMessage({ parlorName: companyName, region, mournerCount })
          }
          // 식장 재방문 비용문의는 항상 true (wiki/features/home-lead-v2.md). 이관 전 동작 그대로.
          forceOutsourcingNightCall
          onClose={() => setIsModalOpen(false)}
          onSubmitted={() => {
            // 이관 전 모달 내부에서 쏘던 이벤트. 데이터 연속성을 위해 이름 유지.
            customEvent({
              newGtm: { path: "parlor_detail", trigger_and_target: "revisit_mdl-submit" },
            });
            markRevisitLeadSubmitted(parlorId);
          }}
        />
      }
    />
  );
};

export default ParlorRevisitExperimentGate;
